import { LOG_MODULES, APP_CONFIG } from "@config/config";
import { log } from '@libs/utils/helper.js';
import { zoomToWells } from './utils';

const logFtTbl = LOG_MODULES.FEATURETABLBE;
const isNeedLog = false;
const zoomForDetail = APP_CONFIG.MAPS.Normal.zoomForDetail;

// Using destructuring in the function signature
export function bindMenuConfig({ table, mapView, highlightLayer, handlersRef, isMapExtentFilterEnabledRef, onModeChange }) {
  if (isNeedLog) log(logFtTbl, "[bindMenuConfig] attaching menuConfig");

  const targetLayer = table.layer;

  // Extract state from the Ref for easier checking
  const hasCheckedItems = handlersRef.current?.checkedRds?.length > 0;
  const hasSelectedItem = !!handlersRef.current?.selectedApiInfo;

  // 1. Built-in menu item visibility.
  // CONFIRMED from official component docs: there is no `visibleElements`
  // property on arcgis-feature-table. Each built-in item is suppressed via
  // its own dedicated boolean property instead.
  table.hideMenuItemsRefreshData = true;
  table.hideMenuItemsDeleteSelection = true;
  table.hideMenuItemsClearSelection = true;
  table.hideMenuItemsZoomToSelection = true;
  table.hideMenuItemsSelectedRecordsShowAllToggle = true;
  table.hideMenuItemsSelectedRecordsShowSelectedToggle = true;
  table.hideMenuItemsExportSelectionToCsv = true;

  // 2. Define all possible custom items
  const allItems = [
    {
      label: "Refresh table",
      icon: "refresh",
      clickFunction: () => {
        table.refresh().then(() => { console.log("Table has been refreshed."); })
          .catch((error) => { console.error("Refresh failed:", error); });
      }
    },
    {
      label: "ReLoad App",
      icon: "refresh-f",
      clickFunction: () => {
        location.reload();
      }
    },
    {
      // "Checked Mode" / "Search Mode" - Hide if no checked records
      label: table.filterBySelectionEnabled ? "Search Mode" : "Checked Mode",
      icon: table.filterBySelectionEnabled ? "add-features" : "selection-filter",
      visible: hasCheckedItems,
      clickFunction: () => {
        table.filterBySelectionEnabled = !table.filterBySelectionEnabled;
        onModeChange?.(table.filterBySelectionEnabled); // ← notify React so search UI can grey out/re-enable

        bindMenuConfig({ table, mapView, highlightLayer, handlersRef, isMapExtentFilterEnabledRef, onModeChange });
      }
    },
    {
      label: "Clear All Items",
      icon: "erase",
      visible: hasCheckedItems || hasSelectedItem,
      clickFunction: () => {
        table.highlightIds.removeAll();
        handlersRef.current.onSingleClick([]);
        handlersRef.current.onDoubleClick?.(null);
        if (highlightLayer) highlightLayer.removeAll();
        // Refresh to hide the buttons since checkedRds is now 0
        bindMenuConfig({ table, mapView, highlightLayer, handlersRef, isMapExtentFilterEnabledRef, onModeChange });
      }
    },
    {
      label: "Zoom to Detailed Item",
      icon: "magnifying-glass-plus",
      visible: hasSelectedItem, // Only visible if an item is selected/detailed
      clickFunction: () => {
        const selectedId = handlersRef.current.selectedApiInfo?.id;
        if (selectedId) zoomToWells(mapView, targetLayer, selectedId, true, highlightLayer, zoomForDetail);
      }
    },
    {
      label: "Zoom to Checked Items",
      icon: "search",
      visible: hasCheckedItems,
      clickFunction: () => {
        table.zoomToSelection(); // confirmed real method, returns Promise<void>
      }
    },
    {
      label: isMapExtentFilterEnabledRef.current ? "Clear Map Extent Filter" : "Filter by Map Extent",
      icon: isMapExtentFilterEnabledRef.current ? "erase" : "check-extent",
      visible: true, // Usually always want to be able to filter by extent
      clickFunction: () => {
        if (!isMapExtentFilterEnabledRef.current) {
          table.filterBySelectionEnabled = false;
          onModeChange?.(false); // ← extent filter forces Search Mode; keep React in sync
          table.filterGeometry = mapView.extent;
          isMapExtentFilterEnabledRef.current = true;
        } else {
          table.filterGeometry = null;
          if (hasCheckedItems) {
            table.filterBySelectionEnabled = true;
            onModeChange?.(true); // ← restoring Checked Mode after clearing extent filter
          }
          isMapExtentFilterEnabledRef.current = false;
        }
        bindMenuConfig({ table, mapView, highlightLayer, handlersRef, isMapExtentFilterEnabledRef, onModeChange });
      }
    }
  ];

  // 3. Filter items based on the 'visible' property before assigning
  table.menuConfig = {
    items: allItems.filter(item => item.visible !== false)
  };
}